import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Inbox, FileText } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-red";

const fmtError = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong");

const Row = ({ label, value }) =>
    value ? (
        <div className="flex gap-3 text-xs">
            <span className="w-36 shrink-0 font-semibold uppercase tracking-wide text-slate-400">{label}</span>
            <span className="break-all text-slate-700">{value}</span>
        </div>
    ) : null;

const Admin = () => {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [tab, setTab] = useState("enquiries");
    const [enquiries, setEnquiries] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(() => setUser(false));
    }, []);

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        Promise.all([
            axios.get(`${API}/admin/enquiries`),
            axios.get(`${API}/admin/quotes`),
        ]).then(([e, q]) => {
            setEnquiries(e.data);
            setQuotes(q.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, [user]);

    const login = async (e) => {
        e.preventDefault();
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            setUser(data);
        } catch (err) {
            setError(fmtError(err.response?.data?.detail));
        }
    };

    const logout = async () => {
        await axios.post(`${API}/auth/logout`).catch(() => {});
        setUser(false);
    };

    if (user === null) {
        return <div className="flex min-h-screen items-center justify-center bg-brand-mist pt-20"><Loader2 className="animate-spin text-brand-red" size={32} /></div>;
    }

    if (!user) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 pt-20 technical-grid-light" data-testid="admin-login-page">
                <form onSubmit={login} className="w-full max-w-sm bg-white p-8 shadow-[0_8px_40px_rgba(15,18,22,0.1)]">
                    <Logo compact />
                    <h1 className="mt-6 font-display text-xl font-extrabold uppercase text-brand-charcoal">Admin Login</h1>
                    <div className="mt-6 space-y-4">
                        <input type="email" required placeholder="Email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                        <input type="password" required placeholder="Password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                    </div>
                    {error && <p className="mt-3 text-sm text-brand-red" data-testid="admin-login-error">{error}</p>}
                    <button type="submit" data-testid="admin-login-submit"
                        className="mt-6 w-full bg-brand-red px-6 py-3.5 font-display text-sm font-bold tracking-[0.14em] text-white transition-colors hover:bg-brand-charcoal">
                        LOGIN
                    </button>
                </form>
            </div>
        );
    }

    const list = tab === "enquiries" ? enquiries : quotes;

    return (
        <div className="min-h-screen bg-brand-mist px-4 pb-20 pt-32 sm:px-6 lg:px-10" data-testid="admin-dashboard">
            <div className="mx-auto max-w-6xl">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-2xl font-extrabold uppercase text-brand-charcoal">Enquiry Dashboard</h1>
                        <p className="mt-1 font-mono text-[11px] tracking-[0.14em] text-slate-500 uppercase">{user.email}</p>
                    </div>
                    <button onClick={logout} data-testid="admin-logout"
                        className="flex items-center gap-2 border border-brand-charcoal px-5 py-2.5 font-display text-xs font-bold tracking-[0.12em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-white">
                        <LogOut size={14} /> LOGOUT
                    </button>
                </div>
                <div className="mt-8 flex gap-2">
                    <button onClick={() => setTab("enquiries")} data-testid="admin-tab-enquiries"
                        className={`flex items-center gap-2 px-5 py-3 font-display text-xs font-bold tracking-[0.12em] transition-colors ${tab === "enquiries" ? "bg-brand-ink text-white" : "bg-white text-brand-charcoal"}`}>
                        <Inbox size={15} /> ENQUIRIES ({enquiries.length})
                    </button>
                    <button onClick={() => setTab("quotes")} data-testid="admin-tab-quotes"
                        className={`flex items-center gap-2 px-5 py-3 font-display text-xs font-bold tracking-[0.12em] transition-colors ${tab === "quotes" ? "bg-brand-ink text-white" : "bg-white text-brand-charcoal"}`}>
                        <FileText size={15} /> QUOTE REQUESTS ({quotes.length})
                    </button>
                </div>
                <div className="mt-6 space-y-4">
                    {loading && <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-red" size={28} /></div>}
                    {!loading && list.length === 0 && (
                        <div className="bg-white p-12 text-center text-sm text-slate-400" data-testid="admin-empty">No {tab} yet.</div>
                    )}
                    {!loading && list.map((item) => (
                        <div key={item.id} className="bg-white p-6 shadow-sm" data-testid={`admin-item-${item.id.slice(0, 8)}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
                                <div>
                                    <p className="font-display text-base font-bold text-brand-charcoal">
                                        {tab === "enquiries" ? item.name : `${item.company} — ${item.contact_person}`}
                                    </p>
                                    <p className="mt-0.5 font-mono text-[10px] tracking-[0.12em] text-slate-400 uppercase">
                                        {new Date(item.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <div className="text-right text-xs text-slate-500">
                                    <a href={`mailto:${item.email}`} className="block text-brand-red hover:underline">{item.email}</a>
                                    <a href={`tel:${item.phone}`} className="block hover:underline">{item.phone}</a>
                                </div>
                            </div>
                            <div className="mt-4 grid gap-2 sm:grid-cols-2">
                                {Object.entries(item)
                                    .filter(([k, v]) => v && !["id", "type", "created_at", "email", "phone", "name", "company", "contact_person", "attachment_path", "attachment_name"].includes(k))
                                    .map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")} value={v} />)}
                            </div>
                            {item.attachment_path && (
                                <a href={`${API}/admin/attachment/${item.attachment_path}`} target="_blank" rel="noreferrer"
                                    data-testid={`admin-attachment-${item.id.slice(0, 8)}`}
                                    className="mt-4 inline-flex items-center gap-2 border border-brand-charcoal px-4 py-2 font-display text-xs font-bold tracking-[0.12em] text-brand-charcoal transition-colors hover:bg-brand-charcoal hover:text-white">
                                    DOWNLOAD ATTACHMENT{item.attachment_name ? ` — ${item.attachment_name}` : ""}
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Admin;
